import '@babylonjs/core/Culling/ray';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { Scene } from '@babylonjs/core/scene';
import { WebXRDefaultExperience } from '@babylonjs/core/XR/webXRDefaultExperience';
import { WebXRSessionManager } from '@babylonjs/core/XR/webXRSessionManager';
import { WebXRState } from '@babylonjs/core/XR/webXRTypes';
import '@babylonjs/core/XR/motionController/webXROculusTouchMotionController';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';

export async function isImmersiveVrSupported(): Promise<boolean> {
  try {
    return await WebXRSessionManager.IsSessionSupportedAsync('immersive-vr');
  } catch {
    return false;
  }
}

/**
 * Standing/seated VR with trigger pointing on both controllers. Table locomotion is handled by
 * xrTableInput, not the default teleport feature. Controller models are not downloaded; the pointer
 * ray is enough until real controller/hand visuals arrive with the asset work. Nor is the list of
 * controller profiles: the Oculus Touch profile is built in, and nothing is fetched from the
 * internet at runtime.
 *
 * Rejects when WebXR does not start. Babylon only logs that and resolves without the base
 * experience, and the scene then failed on its camera (client-audio-engine:bugs#2).
 */
export async function createXr(
  scene: Scene,
  floorMeshes: AbstractMesh[],
): Promise<WebXRDefaultExperience> {
  const xr = await WebXRDefaultExperience.CreateAsync(scene, {
    floorMeshes,
    disableDefaultUI: true,
    disableTeleportation: true,
    disableHandTracking: true,
    disableNearInteraction: true,
    pointerSelectionOptions: {
      enablePointerSelectionOnAllControllers: true,
      // Drawn over the arena HUD (1) and the immersive controls (2), which the laser's end and
      // its dot went behind, leaving no cursor on the plate that holds the volume and Exit VR
      // (x-quest-vr#10). The connection pane shares this group: the ray still meets it in depth.
      renderingGroupId: POINTER_GROUP,
      customLasterPointerMeshGenerator: () => {
        const mesh = MeshBuilder.CreateCylinder(
          'laserPointer',
          { height: 1, diameterTop: 0.0002, diameterBottom: 0.004, tessellation: 12 },
          scene,
        );
        mesh.layerMask = 0x1fffffff;
        return mesh;
      },
      customSelectionMeshGenerator: () => {
        const mesh = MeshBuilder.CreateTorus(
          'gazeTracker',
          { diameter: 0.0105, thickness: 0.0075, tessellation: 12 },
          scene,
        );
        mesh.layerMask = 0x1fffffff;
        return mesh;
      },
    },
    // Babylon's input sets the manager's `UseOnlineRepository` from this option when it starts.
    // Setting the flag on the manager beforehand, as this did, was overwritten, and every
    // controller that connected fetched the profile list from immersive-web.github.io.
    inputOptions: { doNotLoadControllerMeshes: true, disableOnlineControllerRepository: true },
    uiOptions: { sessionMode: 'immersive-vr', referenceSpaceType: 'local-floor' },
  });
  // Typed as always there; it is not when the start failed.
  if (!(xr.baseExperience as WebXRDefaultExperience['baseExperience'] | undefined)) {
    xr.dispose();
    throw new Error('WebXR did not start in this browser');
  }
  return xr;
}

/** The rendering group of the controllers' laser and dot, the topmost Babylon keeps. */
export const POINTER_GROUP = 3;
/**
 * How far the edge of each eye's image is shaded at a lower rate, 0 to 1, where the headset
 * allows it (the Quest's browser does). The lenses blur the edge anyway; the GPU time it gives
 * back is the cheapest headroom a Quest 2 has (client-audio-engine:quality#6).
 */
export const FIXED_FOVEATION = 0.5;

export function enterVr(xr: WebXRDefaultExperience): Promise<void> {
  return xr.baseExperience.enterXRAsync('immersive-vr', 'local-floor', xr.renderTarget).then(() => {
    const session = xr.baseExperience.sessionManager;
    if (session.isFixedFoveationSupported) session.fixedFoveation = FIXED_FOVEATION;
  });
}

/** Calls `onChange(true)` when the headset takes over rendering and `onChange(false)` when it stops. */
export function watchXrState(xr: WebXRDefaultExperience, onChange: (inXr: boolean) => void): void {
  xr.baseExperience.onStateChangedObservable.add((state) => {
    if (state === WebXRState.ENTERING_XR) onChange(true);
    else if (state === WebXRState.NOT_IN_XR) onChange(false);
  });
}
